import { Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { PHBHPLayoutPackageService } from "../../../../ph-bhp-layout-package/lib/ph-bhp-layout-package.service";
import { BsModalRef, BsModalService, ModalOptions } from "ngx-bootstrap/modal";
import { TranslateService } from "@ngx-translate/core";
import { TranslationKeys } from 'ph-bhp-localization';
import { PHBHPFeaturePackageSOPManagementService } from '../../ph-bhp-feature-package-sopmanagement.service';

@Component({
  selector: 'app-step-editor',
  templateUrl: './step-editor.component.html',
  styleUrls: ['./step-editor.component.css']
})
export class StepEditorComponent {

  translationKeys: typeof TranslationKeys = TranslationKeys;
  /**
   * Title of the step
   */
  title: string;
  /**
   * Step data to display.
   */
  stepCards: StepCard[]
  /**
   * Whether or not this step has sub steps.
   */
  isSubProcess: boolean;
  /**
   * The default image of this step. `undefined` if this is NOT a sub process.
   */
  defaultImageIndex?: number;
  /**
   * The image for the step at the specified index should be removed.
   */
  @Output() removeImageForStepIndexClicked = new EventEmitter<number>();
  /**
   * The image for the step at the specified index should be changed.
   */
  @Output() setImageForStepIndexClicked = new EventEmitter<number>();
  // TODO consider unifying the events
  /**
   * The text for situational comments for the step at the specified index should be changed.
   */
  @Output() changeSituationalCommentForStepIndexClicked = new EventEmitter<number>();
  // TODO consider unifying the events
  /**
   * The text for title for the step at the specified index should be changed.
   */
  @Output() changeTitleForStepIndexClicked = new EventEmitter<number>();
  // TODO consider unifying the events
  /**
   * The text for title for the process (entire step) should be changed.
   */
  @Output() changeTitleClicked = new EventEmitter<void>();
  /**
   * The situational option for the step at the specified index should be changed.
   */
  @Output() situationalForStepIndexChanged = new EventEmitter<{ index: number, value: boolean }>();
  /**
   * The "is hygiene relevant" option for the step at the specified index should be changed.
   */
  @Output() hygieneRelevantForStepIndexChanged = new EventEmitter<{ index: number, value: boolean }>();
  /**
   * Any of the hand disinfection indications for this step have been changed.
   * Will return all (new and unchanged) values to simplify interface.
   */
  @Output() handDisinfectionIndicationsForStepIndexChanged = new EventEmitter<{ index: number, value: StepIndications }>();
  @Output() deleteStepClicked = new EventEmitter<void>();
  @Output() deleteSubStepClicked = new EventEmitter<number>();
  @Output() toggleSubProcessClicked = new EventEmitter<boolean>();
  @Output() addSubStepClicked = new EventEmitter<void>();
  /**
   * The image of the specified sub step (index) should be used as default image for the entire step.
   */
  @Output() defaultImageIndexChanged = new EventEmitter<number>()
  /**
   * Temporary variable holding the substep index to be removed, while the confirmation modal is shown.
   */
  deleteSubStepModalState: number;
  /**
   * A reference to the currently opened modal. Make sure to open only one at any time.
   */
  private currentModalRef: BsModalRef;
  /**
   * A reference to the currently step for publish validations
   */
   stepRef: any;

  /*
  * output emiter to update the main sopSteps info (and return to this component)
  */
  @Output() validateCardFields = new EventEmitter<Step>()

  @Output() closeStepEditor = new EventEmitter<void>();

  constructor(
    private layoutService: PHBHPLayoutPackageService,
    private modalService: BsModalService,
    private translate: TranslateService,
    public service: PHBHPFeaturePackageSOPManagementService,
  ) {
  }

  @Input() set step(value: Step | SubProcess) {
      // copy all values to internal fields
      if (this.isEditingStep) return;
      this.title = value.title;
      this.stepRef = value.stepRef;
      if (isSubProcess(value)) {
        this.isSubProcess = true;
        this.stepCards = value.subSteps;
        this.defaultImageIndex = value.useSubStepIndexAsDefaultImage;
      } else {
        this.isSubProcess = false;
        this.stepCards = [value];
        this.defaultImageIndex = undefined;
      }
  }

  @Input() roleId: number;

  isSituational(card: StepCard) {
    // allow any string, including empty string.
    return typeof card.isSituational === 'string';
  }

  editImage(index: number) {
    this.setImageForStepIndexClicked.emit(index);
  }

  removeImage(index: number) {
    this.removeImageForStepIndexClicked.emit(index);
  }

  getFullImageUrl(relativePath: string) {
    return this.layoutService.serverUrl + relativePath;
  }

  hasImage(card: StepCard) {
    return card.imagePath;
  }

  onSituationalCommentsClicked(index: number) {
    this.changeSituationalCommentForStepIndexClicked.emit(index);
  }

  onTitleClicked(index: number, card) {
    // TODO workaround for edit the info into parent (and returned to this component...)
    this.validateCardFields.emit(card);
    this.changeTitleForStepIndexClicked.emit(index);
  }

  onProcessTitleClicked() {
    this.changeTitleClicked.emit();
  }

  onIndicationToggled(index: number, indicationNumber: number, card: StepCard, $event: Event) {
    card['hasIndication'+(indicationNumber)] = !card['hasIndication'+(indicationNumber)]

    this.handDisinfectionIndicationsForStepIndexChanged.emit({
      index,
      value: {
        hasHandDisinfection: card.hasHandDisinfection,
        // update the specific value and also return all previous / unchanged values
        hasIndication1: card.hasIndication1,
        hasIndication2: card.hasIndication2,
        hasIndication3: card.hasIndication3,
        hasIndication4: card.hasIndication4,
        hasIndication5: card.hasIndication5,
      }
    })
  }

  onSituationalToggled(index: number,  card: StepCard, $event: Event) {
    const value = !card.isSituational;

    this.situationalForStepIndexChanged.emit({index, value});
  }

  onHygieneRelevantToggled(index: number,  card: StepCard, $event: Event) {
    const value = !card.isHygieneRelevant;

    this.hygieneRelevantForStepIndexChanged.emit({index, value});
  }

  onHandDisinfectionToggled(index: number, card: StepCard, $event: Event) {
    const value = ($event.target as HTMLInputElement).checked;
    const currentValues = this.stepCards[index];
    card.hasHandDisinfection = !card.hasHandDisinfection;
    this.handDisinfectionIndicationsForStepIndexChanged.emit({
      index,
      value: {
        hasHandDisinfection: card.hasHandDisinfection,
        // the current implementation will keep indications as "set", even when the "group" option (handDisinfection) is disabled
        // TODO consider disabling all individual indications when the group is disabled, as that would be more intuitive for the user
        hasIndication1: currentValues.hasIndication1,
        hasIndication2: currentValues.hasIndication2,
        hasIndication3: currentValues.hasIndication3,
        hasIndication4: currentValues.hasIndication4,
        hasIndication5: currentValues.hasIndication5
      }
    });
  }

  openDeleteSubStepModal(index: number, modalTemplate: TemplateRef<any>) {
    this.deleteSubStepModalState = index;
    this.showModal(modalTemplate, {class: 'w-420 steps-modal'});
  }

  deleteSubStep(index: number) {
    this.deleteSubStepClicked.emit(index);
  }

  openDeleteStepModal(modalTemplate: TemplateRef<any>) {
    this.showModal(modalTemplate, {class: 'w-420 steps-modal'});
  }

  deleteStep() {
    this.deleteStepClicked.emit();
  }

  isEditingStep: boolean = false;

  onFocusOutSOPInput(event: any, key: string) {
    var newValue = event.target.value;
    this.service.getTranslationsFromKey(key).subscribe((result: { language: string, value: string }) => {
      var currentLanguage = this.translate.currentLang;
      if (result['en'] == '' || result['en'] == null) {
        result['en'] = newValue;
        this.translate.set(key, newValue, 'en');
      }

      result[currentLanguage] = newValue;
      this.service.patchTranslation(key, result).subscribe(() => {
        this.translate.set(key, newValue, currentLanguage);
      }, (error) => {}, () => { this.isEditingStep = false; });
    });
  }

  onFocusInInput() {
    this.isEditingStep = true;
  }

  openDisableSubProcessModal(modalTemplate: TemplateRef<any>) {
    if (!this.isSubProcess) {
      // sub process is already disabled
      return;
    }
    this.showModal(modalTemplate, {class: 'w-420'});
  }

  toggleSubProcess(enable: boolean) {
    if (this.isSubProcess == enable) {
      // value does not need to change
      return;
    }
    this.toggleSubProcessClicked.emit(enable);
  }

  restoreSubProcessCheck(){
    this.isSubProcess = false;
    let rb:any = document.getElementById('enable-subprocess-radio');
    rb.checked = true;
  }

  showModal(modalTemplate: TemplateRef<any>, config: ModalOptions) {
    if (this.currentModalRef) {
      // Cannot open multiple modals at once. Rejecting the current modal.
      this.currentModalRef.hide();
    }

    this.currentModalRef = this.modalService.show(
      modalTemplate,
      config);
  }

  hideCurrentModal() {
    this.currentModalRef.hide();
    this.currentModalRef = null;
    this.isEditingStep = false;
  }

  closeStepModal(){
    this.closeStepEditor.emit();
  }

  addSubStep() {
    this.addSubStepClicked.emit();
  }

  isDefaultImage(index: number) {
    if (this.isSubProcess) {
      return this.defaultImageIndex == index;
    }
    return true;
  }

  setAsDefaultImage(index: number) {
    this.defaultImageIndexChanged.emit(index);
  }

  errImg(e:Event){
    e.target['src'] = 'assets/img/fallback-img.png';
  }

  getTranslatedKeyLength(card) {
    if (card.isSituational && card.isSituational.length > 0) {
      var value = this.layoutService.translateText(card.isSituational);
      return value ? value.length : 0;
    } else {
      return 0;
    }
  }
}

// internal types for template

/**
 * A StepCard contains the data of a single step. Can be either the entire (single) step or a sub step.
 *
 * We use the same type in the template, as this simplifies rendering:
 * A "single step" is like a "sub process" with just one step, so we just represent it as a list of step "cards" in the editor.
 *
 * Currently structurally identical to a step, so we use a type alias
 */
type StepCard = Step


// Public api

/**
 * A step of an SOP contains a title, image, and several options to define how it should be performed.
 */
export interface Step extends StepIndications {
  title: string;
  imagePath: string | undefined;
  isHygieneRelevant: boolean;
  /**
   * Whether this Step applies only under a specified condition.
   * If `undefined`, this step is not situational.
   *
   * Not defined as optional (`isSituational?`) to force explicit assignments.
   */
  isSituational: string | undefined;
  stepCount?: string;
  stepRef?: any;
  invalidImage?:boolean;
  invalidTitle?:boolean;
}

/**
 * The toggleable options of a step.
 */
export interface StepIndications {
  hasHandDisinfection: boolean;
  hasIndication1: boolean;
  hasIndication2: boolean;
  hasIndication3: boolean;
  hasIndication4: boolean;
  hasIndication5: boolean;
}

/**
 * A SubProcess is a step of an SOP that itself contains multiple (sub) steps
 * Each sub step has the same structure and options of a "normal" step.
 */
export interface SubProcess {
  title: string;
  subSteps: Step[];
  /**
   * The image of the sub step at this index should be used as the default image for the process / entire step.
   *
   * If undefined, the first image should be used.
   */
  useSubStepIndexAsDefaultImage: number | undefined;
  stepCount?: string;
  stepRef?: any;
  invalidImage?:boolean;
  invalidTitle?:boolean;
}


function isSubProcess(item: Step | SubProcess): item is SubProcess {
  // object is sub process if it provides substeps
  return (item as SubProcess).subSteps !== undefined;
}
